<template>
  <InnerView is-copilot @refresh="handleRefresh">
    <template #header>
      <div class="w-full flex items-center space-x-2 justify-between pr-7">
        <div class="text-body font-bold text-xl">{{ $t('genius.title') }}</div>
        <div class="flex items-center space-x-1">
          <BaseSwitch v-if="shouldDisplayTestModeToggle" v-model:checked="isTestModeActive">
            <template #default>
              <span class="select-none text-sm text-black mr-2">{{ $t('genius.test-mode') }}</span>
            </template>
          </BaseSwitch>
          <BaseButton
            icon="pen-to-square"
            size="sm"
            variant="tertiary"
            iconOnly
            @click="handleNewSession"
          />
        </div>
      </div>
    </template>
    <CopilotContent
      ref="copilotContentRef"
      @go-to-content="handleGoToContent"
      @action="handleAgentAction"
      @get-recommendations="iadvizeStore.getRecommendations"
    />
  </InnerView>
</template>

<script setup lang="ts">
import InnerView from '@commons/components/Layout/InnerView.vue'
import CopilotContent from '@commons/components/Copilot/CopilotContent.vue'
import {
  ExternalContent,
  type LightEntity,
  useAdminStore,
  useCopilotStore
} from '@getmayday/vue-modules'
import { useAgentStore } from '@getmayday/vue-modules/agent'
import { externalContentUri } from '@commons/utils/externalContentsUri'

const { goToEntity } = useEntities()

const iadvizeStore = useHelpdeskStore()
const agentStore = useAgentStore()
const { currentAgentCopilotTool } = storeToRefs(agentStore)

const adminStore = useAdminStore()
const { hasUserPreference } = storeToRefs(adminStore)

const configStore = useConfigStore()
const { geniusTestMode } = storeToRefs(configStore)

const copilotStore = useCopilotStore()
const { focusedTicket } = storeToRefs(copilotStore)

const copilotContentRef = ref<InstanceType<typeof CopilotContent> | null>(null)

const shouldDisplayTestModeToggle = computed(() => {
  return hasUserPreference.value('GENIUS_TEST_MODE') && currentAgentCopilotTool.value
})

const isTestModeActive = computed({
  get() {
    return geniusTestMode.value
  },
  set(value) {
    configStore.setGeniusTestMode(value)
  }
})

const handleGoToContent = (
  content: (LightEntity | ExternalContent) & {
    brain?: boolean
    automation?: boolean
  }
) => {
  if (content.automation) {
    copilotStore.logUseAutomation({
      ticketId: focusedTicket.value || '',
      automation: {
        origin: content.brain ? 'brain' : 'automations',
        documentId: content.id
      },
      openType: 'manual',
      helpdesk: 'iadvize'
    })
  }
  if (content.hasOwnProperty('type')) {
    goToEntity({
      ...(content as LightEntity),
      type:
        (content as any).type === 'attached-contents' ? 'Article' : (content as LightEntity).type
    })
  } else {
    window.open(externalContentUri(content as ExternalContent), '_blank')
  }
}

const handleAgentAction = (action: any) => {
  if (action.type !== 'InjectTicketAnswer') return
  iadvizeStore.injectAnswer(action.payload.ticketAnswer)
}

const handleNewSession = async () => {
  if (!copilotContentRef.value) return
  await copilotContentRef.value.handleNewConversation()
}

const handleRefresh = async () => {
  await Promise.all([agentStore.getAvailableAgents('copilot'), iadvizeStore.getRecommendations()])
}
</script>

<style scoped></style>
